/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/reverseCharge.scss';

import React, {FocusEventHandler, MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import {ChangeFunction, CloseButton, ErrorType, Loader, WithBackground} from '@dvrd/dvr-controls';

import {BolmateButton, BolmateInput} from '../controls';

import CountryField from '../controls/country/field/countryField';

import classNames from 'classnames';

interface Props {
    onChangeVatNumber: ChangeFunction<string>;
    onChangeCountry: ChangeFunction<string>;
    onSubmit: MouseEventHandler;
    onClose: MouseEventHandler;
    onResetError: FocusEventHandler;
    error: ErrorType;
    vatNumber: string;
    country: string;
    active: boolean;
    loading: boolean;
}

export default function ReverseCharge(props: Props) {
    const {t} = useTranslation();
    const {
        error,
        onResetError,
        loading,
        onChangeCountry,
        onChangeVatNumber,
        vatNumber,
        country,
        onClose,
        onSubmit,
        active
    } = props;

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='reverse-charge default-popup'>
                <div className='header-container'>
                    <label className='header-title'>{t('reverseCharge.title', 'BTW verleggen')}</label>
                    <CloseButton onClick={onClose} />
                </div>
                <div className='body-container'>
                    <Loader active={loading} />
                    <p className='description'>
                        {t(
                            'reverseCharge.description',
                            'Voor het verleggen van de BTW is het vereist dat er een juist BTW nummer van de klant op de factuur geplaatst wordt. Controleer hieronder het BTW nummer en het land van de klant voordat de BTW verlegd wordt. De huidige factuur wordt gecrediteerd en er zal een nieuwe (verlegde) factuur aangemaakt worden.'
                        )}
                    </p>
                    <div className='fields'>
                        <BolmateInput
                            onChange={onChangeVatNumber}
                            value={vatNumber}
                            label={t('reverseCharge.vatNumber', 'BTW nummer *')}
                            onFocus={onResetError}
                        />
                        <CountryField onChange={onChangeCountry} value={country} labelAsValue />
                    </div>
                    <label className={classNames('error', error !== null && 'visible')}>
                        {error ?? t('reverseCharge.error', 'Fout')}
                    </label>
                </div>
                <div className='footer-container'>
                    <BolmateButton
                        onClick={onClose}
                        label={t('reverseCharge.cancel', 'Annuleren')}
                        secondary
                        disabled={loading}
                    />
                    <BolmateButton
                        onClick={onSubmit}
                        label={t('reverseCharge.submit', 'BTW verleggen')}
                        disabled={loading}
                    />
                </div>
            </div>
        </WithBackground>
    );
}
